import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';
import './AdminPrintHistory.css';

function AdminPrintHistory() {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0
    });

    const fetchStats = useCallback(async () => {
        try {
            const response = await api.get('/logs/print-history/stats');
            setStats(response.data);
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    }, []);

    const fetchLogs = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', pagination.limit);
            if (filter !== 'all') params.append('action_type', filter);
            if (search) params.append('search', search);
            if (fromDate) params.append('from_date', fromDate);
            if (toDate) params.append('to_date', toDate);

            const response = await api.get(`/logs/print-history?${params.toString()}`);
            setLogs(response.data.logs);
            setPagination(response.data.pagination);
        } catch (err) {
            console.error('Error fetching logs:', err);
        } finally {
            setLoading(false);
        }
    }, [filter, search, fromDate, toDate, pagination.limit]);

    useEffect(() => {
        fetchStats();
        fetchLogs();
    }, [fetchStats, fetchLogs]);

    const getActionName = (action) => {
        const actionNames = {
            'print_slip': 'พิมพ์สลิปเงินเดือน',
            'print_50tawi': 'พิมพ์ 50 ทวิ',
            'download_slip': 'ดาวน์โหลดสลิป',
            'download_50tawi': 'ดาวน์โหลด 50 ทวิ',
            'view_slip': 'ดูสลิปเงินเดือน',
            'view_50tawi': 'ดู 50 ทวิ',
            'request_created': 'สร้างคำร้อง',
            'request_approved': 'อนุมัติคำร้อง',
            'request_rejected': 'ปฏิเสธคำร้อง',
            'request_completed': 'รับเอกสารแล้ว',
            'request_cancelled': 'ยกเลิกคำร้อง'
        };
        return actionNames[action] || action;
    };

    const getActionBadge = (action) => {
        const badges = {
            'print_slip': { class: 'badge-primary', icon: 'print' },
            'print_50tawi': { class: 'badge-info', icon: 'print' },
            'download_slip': { class: 'badge-success', icon: 'download' },
            'download_50tawi': { class: 'badge-success', icon: 'download' },
            'view_slip': { class: 'badge-muted', icon: 'view' },
            'view_50tawi': { class: 'badge-muted', icon: 'view' },
            'request_created': { class: 'badge-warning', icon: 'request' },
            'request_approved': { class: 'badge-success', icon: 'check' },
            'request_rejected': { class: 'badge-error', icon: 'reject' },
            'request_completed': { class: 'badge-info', icon: 'complete' },
            'request_cancelled': { class: 'badge-muted', icon: 'cancel' }
        };
        return badges[action] || { class: 'badge-muted', icon: 'default' };
    };

    const getActionIcon = (type) => {
        const icons = {
            print: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                </svg>
            ),
            download: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                </svg>
            ),
            view: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                </svg>
            ),
            request: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z"/>
                </svg>
            ),
            check: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
            ),
            reject: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
            ),
            complete: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
            ),
            cancel: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>
                </svg>
            ),
            default: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                </svg>
            )
        };
        return icons[type] || icons.default;
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('th-TH', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchLogs(newPage);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchLogs(1);
    };

    return (
        <div className="admin-page">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <span>ประวัติการพิมพ์เอกสาร</span>
            </div>

            <div className="admin-header">
                <h1>ประวัติการพิมพ์เอกสาร</h1>
                <p>ติดตามการพิมพ์และการดาวน์โหลดเอกสารทางการเงินทั้งหมด</p>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="stats-grid mb-3">
                    <div className="stat-card primary">
                        <div className="stat-icon blue">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.today_prints || 0}</div>
                            <div className="stat-label">พิมพ์วันนี้</div>
                        </div>
                    </div>
                    <div className="stat-card warning">
                        <div className="stat-icon orange">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.today_requests || 0}</div>
                            <div className="stat-label">คำร้องวันนี้</div>
                        </div>
                    </div>
                    <div className="stat-card success">
                        <div className="stat-icon green">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.total_slip_prints || 0}</div>
                            <div className="stat-label">สลิปทั้งหมด</div>
                        </div>
                    </div>
                    <div className="stat-card info">
                        <div className="stat-icon purple">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.total_50tawi_prints || 0}</div>
                            <div className="stat-label">50 ทวิทั้งหมด</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="steam-card mb-3">
                <div className="steam-card-body">
                    <form onSubmit={handleSearch}>
                        <div className="filters-row">
                            <div className="filter-tabs">
                                {[
                                    { value: 'all', label: 'ทั้งหมด' },
                                    { value: 'print', label: 'พิมพ์' },
                                    { value: 'download', label: 'ดาวน์โหลด' },
                                    { value: 'request', label: 'คำร้อง' },
                                    { value: 'slip', label: 'สลิป' },
                                    { value: '50tawi', label: '50 ทวิ' }
                                ].map(tab => (
                                    <button
                                        key={tab.value}
                                        type="button"
                                        className={`filter-tab ${filter === tab.value ? 'active' : ''}`}
                                        onClick={() => setFilter(tab.value)}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="filters-row mt-2">
                            <div className="filter-group">
                                <label>จากวันที่</label>
                                <input
                                    type="date"
                                    className="steam-input"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </div>
                            <div className="filter-group">
                                <label>ถึงวันที่</label>
                                <input
                                    type="date"
                                    className="steam-input"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </div>
                            <div className="filter-group flex-grow">
                                <label>ค้นหา</label>
                                <div className="search-input-group">
                                    <input
                                        type="text"
                                        className="steam-input"
                                        placeholder="ค้นหาชื่อ, รหัส หรือรายละเอียด..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <button type="submit" className="steam-btn steam-btn-primary">
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                                            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* Logs Table */}
            <div className="steam-card">
                <div className="steam-card-header">
                    <span>ประวัติ ({pagination.total} รายการ)</span>
                </div>
                <div className="steam-card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="flex-center" style={{ padding: '60px 0' }}>
                            <div className="spinner"></div>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="empty-state">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z"/>
                            </svg>
                            <p>ไม่พบประวัติการพิมพ์เอกสาร</p>
                        </div>
                    ) : (
                        <table className="data-table print-history-table">
                            <thead>
                                <tr>
                                    <th>วันที่/เวลา</th>
                                    <th>ผู้ใช้</th>
                                    <th>กิจกรรม</th>
                                    <th>รายละเอียด</th>
                                    <th>IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => {
                                    const badge = getActionBadge(log.action);
                                    return (
                                        <tr key={log.id}>
                                            <td className="date-cell">
                                                {formatDate(log.created_at)}
                                            </td>
                                            <td>
                                                <div className="user-cell">
                                                    <strong>{log.fullname || '-'}</strong>
                                                    <small>{log.username}</small>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`action-badge ${badge.class}`}>
                                                    {getActionIcon(badge.icon)}
                                                    {getActionName(log.action)}
                                                </span>
                                            </td>
                                            <td className="detail-cell">
                                                {log.action_detail || '-'}
                                            </td>
                                            <td className="ip-cell">
                                                {log.ip_address || '-'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="pagination-container">
                        <button
                            className="pagination-btn"
                            disabled={pagination.page === 1}
                            onClick={() => handlePageChange(pagination.page - 1)}
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                            </svg>
                        </button>
                        <span className="pagination-info">
                            หน้า {pagination.page} / {pagination.totalPages}
                        </span>
                        <button
                            className="pagination-btn"
                            disabled={pagination.page === pagination.totalPages}
                            onClick={() => handlePageChange(pagination.page + 1)}
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                            </svg>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminPrintHistory;
